import { PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { ProjectCard } from './ProjectCard';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Skeleton } from '../../../components/ui/Skeleton';
import type { Database } from '../../../types/database.types';
import type { SortOption } from '../../../pages/DashboardPage';

type Project = Database['public']['Tables']['projects']['Row'];

interface ProjectListProps {
  projects: Project[];
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  onProjectClick: (projectId: number) => void;
  onCreateClick: () => void;
  isLoading?: boolean;
}

/**
 * ProjectList Component
 * Displays list of projects with sorting and empty/loading states
 * 
 * Story: 2.1 - Dashboard/Overview
 * Wireframe: Section 1.4 - Sort Controls, Section 1.5 - Empty State
 */
export const ProjectList = ({
  projects,
  sortBy,
  onSortChange,
  onProjectClick,
  onCreateClick,
  isLoading = false,
}: ProjectListProps) => {
  // Loading state with skeleton screens
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">Projects</h2>
          <Skeleton className="w-48 h-10" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-2 w-full" />
              <div className="flex gap-4">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-12" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!projects || projects.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">Projects</h2>
        </div>
        <EmptyState
          icon={<PlusIcon className="w-8 h-8" />}
          title="No projects yet"
          description="Get started by creating your first project to track your team's work and timelines."
          action={{
            label: 'Create Project',
            onClick: onCreateClick,
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Sort Dropdown and Create Button */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold text-gray-900">
          Projects
          <span className="ml-2 text-sm font-normal text-gray-600">
            ({projects.length} {projects.length === 1 ? 'project' : 'projects'})
          </span>
        </h2>

        {/* Sort Dropdown and Create Button */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="sort-select" className="text-sm font-medium text-gray-700">
              Sort by:
            </label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value as SortOption)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="risk">Risk Status</option>
              <option value="name">Name</option>
              <option value="committed_date">Committed Date</option>
              <option value="progress">Progress</option>
            </select>
          </div>
          <Button
            onClick={onCreateClick}
            variant="primary"
            className="flex items-center gap-2 whitespace-nowrap"
            aria-label="Create new project"
          >
            <PlusIcon className="w-5 h-5" />
            Create Project
          </Button>
        </div>
      </div>

      {/* Project Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={onProjectClick}
            // TODO: Fetch actual task counts and progress from tasks table
            taskCounts={{
              design: 0,
              development: 0,
              qa: 0,
              total: 0,
            }}
            progressPercentage={0}
          />
        ))}
      </div>
    </div>
  );
};

